import { Button, Result, Spin } from 'antd'
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import RequestError from '../../services/RequestError'
import { RequestErrorInterface, SingleResponse } from '../../types'

import styles from './index.module.less'

interface ComponentProps<EntityInterface> {
	entity: EntityInterface
	entityUpdated(newEntity: EntityInterface): void
}

interface EntityRequestConfig<EntityInterface> {
	getById: (id: number) => Promise<SingleResponse<EntityInterface>>
	type: string
}

enum EntityState {
	LOADING,
	ERROR,
	LOADED
}

export default function withEntityRequest<EntityInterface>(
	WrappedComponent: React.FC<ComponentProps<EntityInterface>>,
	{ getById, type }: EntityRequestConfig<EntityInterface>
): React.FC<{ id: number }> {
	return function GetByIdComponent({ id, ...props }: PropsWithChildren<{ id: number }>) {
		const [state, setState] = useState<EntityState>(EntityState.LOADING)
		const [entity, setEntity] = useState<EntityInterface>()
		const [errorMessage, setErrorMessage] = useState<string>()

		const getEntity = useCallback(async (id: number): Promise<void> => {
			const getErrorMessage = (error: RequestErrorInterface): string => {
				const defaultMessage = `We couldn't get ${type} information. Please try again or report this.`
				if (!(error instanceof RequestError)) return defaultMessage
				if (error.response.status === 404) return `${type} not found. This could be due to an administration error.`
				return defaultMessage
			}

			try {
				setState(EntityState.LOADING)
				const { data } = await getById(id)
				setEntity(data)
				setState(EntityState.LOADED)
			} catch (error) {
				setState(EntityState.ERROR)
				setErrorMessage(getErrorMessage(error as RequestErrorInterface))
			}
		}, [])

		const entityUpdated = (newEntity: EntityInterface): void => {
			setEntity(newEntity)
		}

		useEffect(() => {
			getEntity(id)
		}, [getEntity, id])

		if (state === EntityState.LOADING) {
			return <Spin className={styles.center} tip={`Loading ${type}`} />
		}

		if (state === EntityState.ERROR) {
			return <Result status='500' title={`${type} Error`} subTitle={errorMessage} extra={<Button onClick={() => getEntity(id)}>Reload</Button>} />
		}

		return <WrappedComponent entity={entity} entityUpdated={entityUpdated} {...props} />
	}
}
